import { supabase } from '@/integrations/supabase/client';
import type { BundleFilters, BundleWithRelations, BundleItem, Retailer, BundleKitType, BundleVersion } from '@/contracts/bundles';

// ============= Retailers =============
export async function getRetailers(): Promise<Retailer[]> {
  const { data, error } = await supabase
    .from('retailers')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });
  
  if (error) throw error;
  return data || [];
}

// ============= Kit Types =============
export async function getKitTypes(): Promise<BundleKitType[]> {
  const { data, error } = await supabase
    .from('bundle_kit_types')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });
  
  if (error) throw error;
  return data || [];
}

// ============= Versions =============
export async function getBundleVersions(): Promise<BundleVersion[]> {
  const { data, error } = await supabase
    .from('bundle_versions')
    .select('*')
    .order('sort_order', { ascending: true });
  
  if (error) throw error;
  return data || [];
}

// ============= Bundles =============
export async function getBundles(filters: BundleFilters): Promise<BundleWithRelations[]> {
  let query = supabase
    .from('bundles')
    .select(`
      *,
      retailer:retailers(*),
      kit_type:bundle_kit_types(*),
      version:bundle_versions(*),
      items:bundle_items(*)
    `);

  if (filters.is_published !== undefined) {
    query = query.eq('is_published', filters.is_published);
  }

  if (filters.retailer_slug) {
    query = query.eq('retailers.slug', filters.retailer_slug);
  }

  if (filters.kit_type_slug) {
    query = query.eq('bundle_kit_types.slug', filters.kit_type_slug);
  }

  if (filters.version_slug) {
    query = query.eq('bundle_versions.slug', filters.version_slug);
  }

  if (filters.max_price) {
    query = query.lte('total_price_max', filters.max_price);
  }

  if (filters.search) {
    query = query.or(
      `summary.ilike.%${filters.search}%,` +
      `kit_type.name.ilike.%${filters.search}%,` +
      `retailer.name.ilike.%${filters.search}%`
    );
  }

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .range(filters.offset, filters.offset + filters.limit - 1);

  if (error) throw error;
  return (data || []) as BundleWithRelations[];
}

export async function getBundleBySlug(slug: string): Promise<BundleWithRelations | null> {
  const { data, error } = await supabase
    .from('bundles')
    .select(`
      *,
      retailer:retailers(*),
      kit_type:bundle_kit_types(*),
      version:bundle_versions(*),
      items:bundle_items(*)
    `)
    .eq('slug', slug)
    .maybeSingle();

  if (error) throw error;
  return data as BundleWithRelations | null;
}

export async function incrementBundleViews(bundleId: string): Promise<void> {
  // Fetch current count
  const { data: bundle } = await supabase
    .from('bundles')
    .select('views_count')
    .eq('id', bundleId)
    .single();
  
  if (!bundle) return;
  
  // Increment
  const { error } = await supabase
    .from('bundles')
    .update({ views_count: bundle.views_count + 1 })
    .eq('id', bundleId);
  
  if (error) console.error('Failed to increment views:', error);
}

// ============= Bundle Items =============
export async function getBundleItems(bundleId: string): Promise<BundleItem[]> {
  const { data, error } = await supabase
    .from('bundle_items')
    .select('*')
    .eq('bundle_id', bundleId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

// ============= Tracking =============
export async function logBundleClick(
  bundleId: string,
  itemId: string | null,
  metadata?: { user_agent?: string; referrer?: string }
): Promise<void> {
  const { error } = await supabase
    .from('bundle_clicks')
    .insert({
      bundle_id: bundleId,
      item_id: itemId,
      user_agent: metadata?.user_agent || navigator.userAgent,
      referrer: metadata?.referrer || document.referrer,
    });

  if (error) console.error('Failed to log click:', error);
  
  // Also increment bundle clicks_count
  const { data: bundle } = await supabase
    .from('bundles')
    .select('clicks_count')
    .eq('id', bundleId)
    .single();
    
  if (bundle) {
    await supabase
      .from('bundles')
      .update({ clicks_count: bundle.clicks_count + 1 })
      .eq('id', bundleId);
  }
}

// ============= CRUD Operations =============
export async function updateBundle(
  bundleId: string,
  updates: {
    summary?: string;
    total_price_min?: number;
    total_price_max?: number;
    is_published?: boolean;
  }
): Promise<void> {
  const { error } = await supabase
    .from('bundles')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', bundleId);

  if (error) throw error;
}

export async function updateBundleItem(
  itemId: string,
  updates: Partial<BundleItem>
): Promise<void> {
  const { error } = await supabase
    .from('bundle_items')
    .update(updates)
    .eq('id', itemId);

  if (error) throw error;
}

export async function addBundleItem(
  bundleId: string,
  item: Omit<BundleItem, 'id' | 'created_at' | 'bundle_id'>
): Promise<BundleItem> {
  const { data, error } = await supabase
    .from('bundle_items')
    .insert([{ 
      bundle_id: bundleId,
      name: item.name,
      description: item.description,
      search_query: item.search_query,
      price_range: item.price_range,
      sort_order: item.sort_order,
      estimated_price: item.estimated_price,
      category: item.category,
      image_url: item.image_url,
    }])
    .select()
    .single();

  if (error) throw error;

  // Increment bundle item count
  const { data: bundle } = await supabase
    .from('bundles')
    .select('item_count')
    .eq('id', bundleId)
    .single();

  if (bundle) {
    await supabase
      .from('bundles')
      .update({ item_count: bundle.item_count + 1 })
      .eq('id', bundleId);
  }

  return data;
}

export async function deleteBundleItem(itemId: string): Promise<void> {
  // Get bundle_id before deleting
  const { data: item } = await supabase
    .from('bundle_items')
    .select('bundle_id')
    .eq('id', itemId)
    .single();

  const { error } = await supabase
    .from('bundle_items')
    .delete()
    .eq('id', itemId);

  if (error) throw error;

  // Decrement bundle item count
  if (item) {
    const { data: bundle } = await supabase
      .from('bundles')
      .select('item_count')
      .eq('id', item.bundle_id)
      .single();

    if (bundle) {
      await supabase
        .from('bundles')
        .update({ item_count: Math.max(0, bundle.item_count - 1) })
        .eq('id', item.bundle_id);
    }
  }
}

export async function deleteBundle(bundleId: string): Promise<void> {
  // Delete bundle items first (if FK doesn't cascade)
  await supabase
    .from('bundle_items')
    .delete()
    .eq('bundle_id', bundleId);

  // Delete bundle
  const { error } = await supabase
    .from('bundles')
    .delete()
    .eq('id', bundleId);

  if (error) throw error;
}

/**
 * Toggle bundle published status
 */
export async function toggleBundlePublished(
  bundleId: string,
  currentStatus: boolean
): Promise<void> {
  const { error } = await supabase
    .from('bundles')
    .update({ 
      is_published: !currentStatus,
      updated_at: new Date().toISOString(),
    })
    .eq('id', bundleId);

  if (error) throw error;
}

/**
 * Create new bundle
 */
export async function createBundle(data: {
  slug: string;
  retailer_id: string;
  kit_type_id: string;
  version_id: string;
  summary: string;
  total_price_min?: number;
  total_price_max?: number;
}): Promise<void> {
  const { error } = await supabase.from('bundles').insert({
    slug: data.slug,
    retailer_id: data.retailer_id,
    kit_type_id: data.kit_type_id,
    version_id: data.version_id,
    summary: data.summary,
    total_price_min: data.total_price_min || null,
    total_price_max: data.total_price_max || null,
  });

  if (error) throw error;
}
